// 2) Third-party libraries
import { createAction, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

// 3) Local imports
// 3.1 Constants
import { DETAILS_TAB, WORKFLOW_CONFIGURATIONS_PAGE } from "../../config/tableAndPageConstants";
import {
    workflowChangesTableFilterOptions,
} from '../../constants/workflowsSortAndFilters';

// 3.3 Project-specific modules � components/types
import {
    DeleteWorkflowsRequestPayload,
    GetWorkflowChangesRequestPayload,
    GetWorkflowConfigurationsRequestPayload,
    GetWorkflowDefinitionRequestPayload,
    UpdateWorkflowRequestPayload,
    WorkflowChanges,
    WorkflowConfiguration,
    WorkflowDefinition,
    WorkflowsState,
} from "./types";

// 3.4 Utility functions / types
import { ResponseModalObject } from "../../utils/commonInterfaces";

// 3.5 Redux actions / store
import type { RootState } from "../storeTypes";
import { get, isEqual, uniqWith } from "lodash-es";
import { PageData } from "../common/types";

const actionPrefix = 'workflow';

// Type-safe initialState!
export const initialState: WorkflowsState = {
    loading: false,
    saveWorkflowDataLoading: false,
    errorMessages: [],
    data: [],
    filters: {},
    sortAscending: true,
    tableFilter: undefined,
    pageData: {
        pageSize: WORKFLOW_CONFIGURATIONS_PAGE.pageSize,
        currentPage: 0,
        hasNextPage: false,
    },
    activeData: {
        record: undefined,
        loading: false,
        selectedId: null,
        errorMessages: [],
        workflowChanges: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.WORKFLOW_CHANGES_TIMELINE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            workflowChangesTableFilter: get(
                workflowChangesTableFilterOptions,
                '0.value'
            ),
            sortBy: 'Created Date Time',
            sortAscending: false,
        }
    }
};

const workflowSlice = createSlice({
    name: actionPrefix,
    initialState,
    reducers: {
        getWorkflowConfigurationsRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    filters: any;
                    sortAscending: boolean;
                    pageSize: number;
                    currentPage: number;
                    callback?: (fetched: WorkflowConfiguration[]) => void;
                }>
            ) => {
                state.loading = true;
            },
            prepare: (
                payload: GetWorkflowConfigurationsRequestPayload,
                callback?: (fetched: WorkflowConfiguration[]) => void
            ) => ({
                payload: {
                    ...payload,
                    callback
                }
            })
        },
        getWorkflowConfigurationsSuccessAction: (
            state,
            action: PayloadAction<{
                data: WorkflowConfiguration[];
            }>
        ) => {
            state.loading = false;
            state.data = action.payload.data;
            state.pageData = initialState.pageData;
            state.errorMessages = initialState.errorMessages;
        },
        getWorkflowConfigurationsErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.loading = false;
            state.data = initialState.data;
            state.errorMessages = action.payload;
        },
        setWorkflowSelectedIdRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    workflowId: string | null;
                    callback: () => void;
                }>
            ) => {
                state.activeData.selectedId = initialState.activeData.selectedId;
            },
            prepare: (
                workflowId: string | null,
                callback: () => void
            ) => ({
                payload: {
                    workflowId,
                    callback
                }
            })
        },
        setWorkflowSelectedIdSuccessAction: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.activeData.selectedId = action.payload;
        },
        getWorkflowDefinitionRequestAction: (
            state,
            _action: PayloadAction<GetWorkflowDefinitionRequestPayload>
        ) => {
            state.activeData.loading = true;
        },
        getWorkflowDefinitionSuccessAction: (
            state,
            action: PayloadAction<WorkflowDefinition>
        ) => {
            state.activeData.loading = false;
            state.activeData.errorMessages = initialState.activeData.errorMessages;
            state.activeData.record = action.payload;
        },
        getWorkflowDefinitionErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.loading = false;
            state.activeData.errorMessages = action.payload;
            state.activeData.record = initialState.activeData.record;
        },
        clearWorkflowsStateAction: (state) => {
            Object.assign(state, initialState);
        },
        clearWorkflowActiveDataAction: (state) => {
            state.activeData = initialState.activeData;
        },
        getWorkflowChangesRequestAction: (
            state,
            _action: PayloadAction<GetWorkflowChangesRequestPayload>
        ) => {
            state.activeData.workflowChanges.loading = true;
        },
        getWorkflowChangesSuccessAction: (
            state,
            action: PayloadAction<{
                data: WorkflowChanges[];
                pageData: PageData;
            }>
        ) => {
            const { data, pageData } = action.payload;
            let newDataState: any[] = [];

            if (pageData.currentPage === 0) {
                newDataState = data;
            } else {
                newDataState = uniqWith(
                    [...state.activeData.workflowChanges.data, ...data],
                    isEqual
                );
            }

            state.activeData.workflowChanges.loading = false;
            state.activeData.workflowChanges.data = newDataState;
            state.activeData.workflowChanges.pageData = pageData;
            state.activeData.workflowChanges.errorMessages = initialState.activeData.workflowChanges.errorMessages;
        },
        getWorkflowChangesErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.workflowChanges.loading = false;
            state.activeData.workflowChanges.data = initialState.activeData.workflowChanges.data;
            state.activeData.workflowChanges.errorMessages = action.payload;
        },
        updateWorkflowChangesFiltersAction: (
            state,
            action: PayloadAction<GetWorkflowChangesRequestPayload['filters']>
        ) => {
            state.activeData.workflowChanges.filters = action.payload;
        },
        updateWorkflowChangesTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.workflowChanges.workflowChangesTableFilter = action.payload;
        },
        updateWorkflowChangesSortByAction: (
            state,
            action: PayloadAction<{
                sortBy: GetWorkflowChangesRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.activeData.workflowChanges.sortBy = action.payload.sortBy;
            state.activeData.workflowChanges.sortAscending = action.payload.sortAscending;
        }
    }
});

const { actions, reducer } = workflowSlice;

// Actions
export const {
    getWorkflowConfigurationsRequestAction,
    getWorkflowConfigurationsSuccessAction,
    getWorkflowConfigurationsErrorAction,
    setWorkflowSelectedIdRequestAction,
    setWorkflowSelectedIdSuccessAction,
    getWorkflowDefinitionRequestAction,
    getWorkflowDefinitionSuccessAction,
    getWorkflowDefinitionErrorAction,
    clearWorkflowsStateAction,
    clearWorkflowActiveDataAction,
    getWorkflowChangesRequestAction,
    getWorkflowChangesSuccessAction,
    getWorkflowChangesErrorAction,
    updateWorkflowChangesFiltersAction,
    updateWorkflowChangesTableFilterAction,
    updateWorkflowChangesSortByAction
} = actions;

export const saveWorkflowDefinitionConfigurationRequestAction = createAction(
    `${actionPrefix}/saveWorkflowDefinitionConfigurationRequestAction`,
    function prepare(
        payload: any,
        callback?: (response: ResponseModalObject) => void
    ) {
        return {
            payload: { payload, callback }
        };
    }
);

export const updateWorkflowRequestAction = createAction(
    `${actionPrefix}/updateWorkflowRequestAction`,
    function prepare(
        payload: UpdateWorkflowRequestPayload,
        isCreateNew: boolean,
        callback?: (response: ResponseModalObject) => void
    ) {
        return {
            payload: { payload, isCreateNew, callback }
        };
    }
);

export const deleteWorkflowsRequestAction = createAction(
    `${actionPrefix}/deleteWorkflowsRequestAction`,
    function prepare(
        payload: DeleteWorkflowsRequestPayload
    ) {
        return {
            payload
        };
    }
);

// Selectors
export const getWorkflowsState = (state: RootState) => state.workflows;
export const getWorkflowConfigurationsData = createSelector(
    getWorkflowsState,
    (workflows) => workflows.activeData
);
export const getWorkflowSelectedId = createSelector(
    getWorkflowConfigurationsData,
    (activeData) => activeData.selectedId
);

// Reducer
export default reducer;